import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent } from '@/components/ui';
import { Button, Input, Label, Spinner } from '@/components/ui';
import { PasswordRequirements, isPasswordValid } from '@/components/features/auth/password-requirements';

/**
 * Challenge-State aus SessionStorage lesen.
 * Gespeichert von login.tsx bei NEW_PASSWORD_REQUIRED Challenge.
 */
const CHALLENGE_STATE_KEY = 'auth_challenge_state';
const CHALLENGE_TTL_MS = 5 * 60 * 1000; // 5 Minuten

interface ChallengeState {
  email: string;
  timestamp: number;
}

function getChallengeState(): ChallengeState | null {
  try {
    const raw = sessionStorage.getItem(CHALLENGE_STATE_KEY);
    if (!raw) return null;

    const state: ChallengeState = JSON.parse(raw);

    // TTL pruefen — abgelaufene States verwerfen
    if (Date.now() - state.timestamp > CHALLENGE_TTL_MS) {
      sessionStorage.removeItem(CHALLENGE_STATE_KEY);
      return null;
    }

    return state;
  } catch {
    sessionStorage.removeItem(CHALLENGE_STATE_KEY);
    return null;
  }
}

/**
 * Erstlogin-Passwort-Aenderung nach NEW_PASSWORD_REQUIRED Challenge.
 * Nur erreichbar, wenn ein gueltiger Challenge-State in SessionStorage liegt.
 *
 * @see Spec 0043 Section 4.3
 */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { confirmNewPassword } = useAuth();

  const [challengeState, setChallengeState] = useState<ChallengeState | null>(null);
  const [checked, setChecked] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bei Mount: Challenge-State lesen
  useEffect(() => {
    const state = getChallengeState();
    setChallengeState(state);
    setChecked(true);

    if (!state) {
      // Kein Challenge-State oder abgelaufen → zurueck zum Login mit Hinweis
      navigate('/login?expired=challenge', { replace: true });
    }
  }, [navigate]);

  if (!checked || !challengeState) {
    return null; // Redirect laeuft
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError('Das Passwort erfüllt nicht alle Anforderungen.');
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmNewPassword(newPassword);
      // Cleanup: Challenge-State entfernen
      sessionStorage.removeItem(CHALLENGE_STATE_KEY);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'InvalidPasswordException') {
          setError('Das Passwort erfüllt nicht die Anforderungen.');
        } else if (err.name === 'NotAuthorizedException') {
          // Session abgelaufen — zurueck zum Login
          sessionStorage.removeItem(CHALLENGE_STATE_KEY);
          navigate('/login', { replace: true });
        } else {
          setError('Passwortänderung fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-8 pb-6">
          <h1 className="mb-2 text-center text-lg font-semibold">
            Passwort ändern
          </h1>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            Bitte ändern Sie Ihr temporäres Passwort.
          </p>

          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-center text-sm text-destructive">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoFocus
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? 'Verbergen' : 'Anzeigen'}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <PasswordRequirements password={newPassword} />

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <span className="flex items-center gap-2"><Spinner size="sm" />Speichern…</span>
              ) : (
                'Passwort ändern'
              )}
            </Button>
          </form>

          {/* Kein Zurueck-Button — User MUSS Passwort aendern (Spec 0043) */}
        </CardContent>
      </Card>
    </div>
  );
}
